import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FcIcons from 'react-icons/fc';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import { getAuthProviders } from '../../api';

const { FiX, FiMail, FiLock, FiUser, FiArrowRight, FiShield, FiInfo } = FiIcons;
const { FcGoogle } = FcIcons;

const AuthModal = ({ isOpen, onClose }) => {
  const [isLogin, setIsLogin]       = useState(true);
  const [providers, setProviders]   = useState(null);
  const [email, setEmail]           = useState('');
  const [password, setPassword]     = useState('');
  const [name, setName]             = useState('');
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const { login, signup }           = useAuth();

  useEffect(() => {
    if (isOpen) {
      getAuthProviders().then(setProviders).catch(() => setProviders({ email: true }));
      // Reset form on open
      setEmail(''); setPassword(''); setName(''); setError('');
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password, name);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    const instance  = '53179_memorial_sanctuary';
    const callback  = encodeURIComponent(window.location.origin + '/#/explore');
    window.location.href = `https://app.nocodebackend.com/api/user-auth/sign-in/social?provider=google&Instance=${instance}&callbackURL=${callback}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <AnimatePresence mode="wait">
        <motion.div
          key="auth-modal"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl"
        >
          <div className="relative p-8">
            <button onClick={onClose}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors">
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>

            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl mb-4">
                <SafeIcon icon={FiShield} className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-slate-500 dark:text-slate-400 mt-2">
                {isLogin ? 'Access your memorial sanctuary' : 'Start preserving a legacy today'}
              </p>
            </div>

            {/* Role notice for signup */}
            {!isLogin && (
              <div className="mb-5 flex items-start gap-3 p-3.5 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl">
                <SafeIcon icon={FiInfo} className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-indigo-700 dark:text-indigo-300 leading-relaxed">
                  New accounts are created with the <strong>Memorial Creator</strong> role.
                  Site Administrator access can only be granted by a Super Admin.
                </p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse flex-shrink-0" />
                {error}
              </div>
            )}

            {providers?.google && (
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-700 dark:text-slate-200 font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-all mb-6"
              >
                <SafeIcon icon={FcGoogle} className="w-5 h-5" />
                Continue with Google
              </button>
            )}

            {(providers?.google && providers?.email) && (
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-800" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="px-2 bg-white dark:bg-slate-900 text-slate-500">Or continue with email</span>
                </div>
              </div>
            )}

            {providers?.email && (
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="relative text-slate-400 focus-within:text-indigo-600 transition-colors">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white transition-all outline-none"
                    />
                  </div>
                )}
                <div className="relative text-slate-400 focus-within:text-indigo-600 transition-colors">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    type="email"
                    placeholder="Email Address"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white transition-all outline-none"
                  />
                </div>
                <div className="relative text-slate-400 focus-within:text-indigo-600 transition-colors">
                  <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" />
                  <input
                    type="password"
                    placeholder="Password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-indigo-500 text-slate-900 dark:text-white transition-all outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-xl transition-all group"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {isLogin ? 'Sign In' : 'Create Creator Account'}
                      <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </form>
            )}

            <div className="mt-8 text-center text-sm text-slate-500">
              {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
              <button
                onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-indigo-600 dark:text-indigo-400 font-semibold hover:underline"
              >
                {isLogin ? 'Sign Up' : 'Sign In'}
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default AuthModal;